import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import MessageBubble from 'components/_shared/MessageBubble';

import Button from '@mui/material/Button';
// import TextAreaAutosize from '@mui/material/TextareaAutosize';
import { createStyles, makeStyles } from '@mui/styles';

const useStyles = makeStyles(() =>
    createStyles({
        checkButton: {
            position: 'absolute',
            right: '-20px',
            background:
                'linear-gradient(90deg, rgba(184, 194, 255, 0.0001) 0%, #B8C2FF 100%), #9897E3',
            borderRadius: '36px',
            boxShadow: '-2.44929e-16px 4px 20px rgba(0, 0, 0, 0.121569)',
            color: '#FFFFFF',
            fontSize: '28px',
            padding: '22px',
            '&:hover': {
                background:
                    'linear-gradient(90deg, rgba(184, 194, 255, 0.0001) 0%, #B8C2FF 100%), #9897E3'
            }
        },
        customContainer: {},
        customInput: {
            border: 'none',
            color: '#1F1F51',
            fontFamily: 'Manrope',
            fontSize: '30px',
            fontWeight: 600,
            lineHeight: '45px',
            marginTop: '10px',
            outline: 'none',
            padding: '12px 10px',
            resize: 'none',
            textDecoration: 'underline',
            textDecorationColor: '#E3E4FA',
            '&::placeholder': {
                color: '#A8A8BF',
                fontStyle: 'italic',
                fontWeight: '300',
                letterSpacing: '0.02em',
                opacity: '0.7',
                textDecoration: 'underline',
                textDecorationColor: '#E3E4FA'
            }
        },
        customInputContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        },
        customButton: {
            border: '2px dashed #EAEBFB',
            borderRadius: '40px',
            color: '#A8A8BF',
            fontSize: '16px',
            fontWeight: 300,
            letterSpacing: '0.01em',
            marginTop: '10px',
            opacity: '0.7',
            padding: '5px 20px',
            textDecoration: 'underline',
            textTransform: 'none',
            '&:hover': {
                textDecoration: 'underline'
            }
        },
        doneButton: {
            border: '3px solid #E3E4FA',
            borderRadius: '36px',
            fontSize: '14px',
            fontWeight: 800,
            padding: '4px 28px 4px 14px',
            textTransform: 'none'
        },
        questionBody: {
            marginBottom: '120px'
        },
        suggestion: {
            background: '#EAEBFB',
            borderRadius: '32px',
            color: '#1F1F51',
            cursor: 'pointer',
            fontSize: '14px',
            width: 'fit-content',
            letterSpacing: '0.01em',
            opacity: '0.7',
            padding: '10px 20px',
            textTransform: 'none'
        },
        suggestionContainer: {
            display: 'flex',
            flexDirection: 'column',
            background: '#FFFFFF',
            borderRadius: '20px',
            padding: '20px 30px',
            width: '75%',
            margin: 'auto',
            boxShadow: '0px 10px 80px rgba(0, 0, 0, 0.1)',
            position: 'relative'
        },
        suggestionList: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            marginTop: '10px'
        },
        suggestionTitle: {
            position: 'relative',
            left: '11%',
            top: '0.5em',
            background: '#8882D8',
            borderRadius: '32px',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 800,
            letterSpacing: '0.05em',
            padding: '5px 20px',
            textTransform: 'uppercase',
            zIndex: 2
        }
    })
);

const useDebounce = (callback: Function, delay: number) => {
    const debounceRef = useRef<null | NodeJS.Timeout>(null);

    const debouncedCallback = useCallback(
        (...args: any[]) => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }

            debounceRef.current = setTimeout(() => {
                callback(...args);
            }, delay);
        },
        [callback, delay]
    );

    return debouncedCallback;
};

type QuestionBodyProps = {
    answers: {
        answer: string[];
        id: number;
    }[];
    questions: {
        id: number;
        suggestions: string;
        question: string;
        multi_select_options: boolean;
    };
    questionLength: number;
    questionNum: number;
    setAnswers: React.Dispatch<
        React.SetStateAction<
            {
                answer: string[];
                id: number;
            }[]
        >
    >;
    intakeTouchpoints: {
        answer: string;
        questionId: number;
    }[];
    setIntakeTouchpoints: (questionId: number, answer: string) => void;
};

const QuestionBody: React.FC<QuestionBodyProps> = ({
    questions,
    questionLength,
    questionNum,
    setAnswers,
    intakeTouchpoints,
    setIntakeTouchpoints
}) => {
    const classes = useStyles();
    // const inputRef = useRef<HTMLTextAreaElement>(null);
    const questionRef = useRef(null);

    // const [customAnswer, setCustomAnswer] = useState('');
    const [customAnswerList, setCustomAnswerList] = useState<string[]>([]);
    const [isActive, setIsActive] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState<string[]>([]);
    // const [showCustomInput, setShowCustomInput] = useState(false);

    const { suggestions, question, id, multi_select_options } = questions;
    const suggestionList = useMemo(
        () => suggestions.split(', '),
        [suggestions]
    );

    const handleIntersectionDebounced = useDebounce(
        ([entry]: IntersectionObserverEntry[]) => {
            setIsActive(entry.isIntersecting);
        },
        200 // Adjust the debounce delay as needed
    );

    const renderSelectedAnswerStyle = (answer: string) => {
        if (selectedAnswer.includes(answer)) {
            return {
                background: '#9DA2F7',
                color: '#FFFFFF',
                opacity: '1'
            };
        }
    };

    const selectAnswer = useCallback((answer: string) => {
        if (multi_select_options === false) {
            setSelectedAnswer([answer]);
            return;
        }

        setSelectedAnswer((prevSelectedAnswer) => {
            if (prevSelectedAnswer.includes(answer)) {
                return prevSelectedAnswer.filter(
                    (prevAnswer) => prevAnswer !== answer
                );
            } else {
                return [...prevSelectedAnswer, answer];
            }
        });
    }, [multi_select_options]);

    // const saveCustomInput = useCallback(() => {
    //     if (!customAnswer.length) {
    //         setShowCustomInput(false);
    //         return;
    //     }

    //     setCustomAnswerList((prev) => [...prev, customAnswer]);
    //     selectAnswer(customAnswer);
    //     setCustomAnswer('');
    //     setShowCustomInput(false);
    // }, [customAnswer, selectAnswer]);

    const updateAnswers = useCallback(() => {
        setAnswers((prevAnswers) => {
            const existingAnswerIndex = prevAnswers?.findIndex(
                (prevAnswer) => prevAnswer.id === id
            );

            if (selectedAnswer.length === 0) {
                return prevAnswers.filter((prevAnswer) => prevAnswer.id !== id);
            } else if (existingAnswerIndex !== -1) {
                const updatedAnswers = [...prevAnswers];

                updatedAnswers[existingAnswerIndex] = {
                    ...updatedAnswers[existingAnswerIndex],
                    answer: selectedAnswer
                };

                return updatedAnswers;
            } else {
                return [
                    ...prevAnswers,
                    {
                        answer: selectedAnswer,
                        id
                    }
                ];
            }
        });

        setIntakeTouchpoints(id, selectedAnswer.join(', '));
    }, [id, selectedAnswer, setAnswers, setIntakeTouchpoints]);

    // useEffect(() => {
    //     if (inputRef.current && showCustomInput) {
    //         inputRef.current.focus();
    //     }
    // }, [showCustomInput]);

    useEffect(() => {
        if (selectedAnswer.length) {
            updateAnswers();
        }
    }, [selectedAnswer, updateAnswers]);

    useEffect(() => {
        selectedAnswer.forEach((answer) => {
            if (
                !suggestionList.includes(answer) &&
                !customAnswerList.includes(answer)
            ) {
                setCustomAnswerList((prev) => [...prev, answer]);
            }
        });
    }, [selectedAnswer, suggestionList, customAnswerList, setCustomAnswerList]);

    useEffect(() => {
        if (intakeTouchpoints.length) {
            intakeTouchpoints.forEach((touchpoint) => {
                if (touchpoint.questionId === id) {
                    setSelectedAnswer((prevSelectedAnswer) => {
                        const newSelectedAnswer = touchpoint.answer.split(', ');

                        if (
                            JSON.stringify(prevSelectedAnswer) !==
                            JSON.stringify(newSelectedAnswer)
                        ) {
                            return newSelectedAnswer;
                        }

                        return prevSelectedAnswer;
                    });
                }
            });
        }
    }, [id, intakeTouchpoints]);

    useEffect(() => {
        const observer = new IntersectionObserver(handleIntersectionDebounced, {
            threshold: 0.7
        });
        const questionActive = questionRef.current;

        if (questionActive) {
            observer.observe(questionActive);
        }

        return () => {
            if (questionActive) {
                observer.unobserve(questionActive);
            }
        };
    }, [handleIntersectionDebounced]);

    return (
        <div
            className={classes.questionBody}
            ref={questionRef}
            id={id.toString()}
            style={{ opacity: isActive ? 1 : 0.4, transition: 'opacity 0.4s' }}
        >
            <MessageBubble
                question={question}
                questionLength={questionLength}
                questionNum={questionNum}
            />
            <span className={classes.suggestionTitle}>
                {multi_select_options === false ? 
                    "Select one option"
                : 
                    "Click All That Apply"}
            </span>
            <div className={classes.suggestionContainer}>
                <div className={classes.suggestionList}>
                    {suggestionList.map((suggestion, index) => (
                        <Button
                            className={classes.suggestion}
                            key={index}
                            onClick={() => selectAnswer(suggestion)}
                            style={renderSelectedAnswerStyle(suggestion)}
                        >
                            {suggestion}
                        </Button>
                    ))}
                    {customAnswerList.map((answer, index) => (
                        <Button
                            className={classes.suggestion}
                            key={index}
                            onClick={() => selectAnswer(answer)}
                            style={renderSelectedAnswerStyle(answer)}
                        >
                            {answer}
                        </Button>
                    ))}
                </div>
                {/* <div className={classes.customContainer}>
                    {!showCustomInput ? (
                        <Button
                            className={classes.customButton}
                            onClick={() => setShowCustomInput(true)}
                        >
                            Write your own...
                        </Button>
                    ) : (
                        <div className={classes.customInputContainer}>
                            <TextAreaAutosize
                                className={classes.customInput}
                                onChange={(e) =>
                                    setCustomAnswer(e.target.value)
                                }
                                placeholder={'Write your own...'}
                                ref={inputRef}
                                spellCheck={'false'}
                                value={customAnswer}
                            />
                            <div className={classes.doneButton}>Done</div>
                            <Button
                                className={classes.checkButton}
                                onClick={() => saveCustomInput()}
                            >
                                <i className="fas fa-check" />
                            </Button>
                        </div>
                    )}
                </div> */}
            </div>
        </div>
    );
};

export default QuestionBody;